import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Alert,
  Snackbar,
  Stack,
} from "@mui/material";
import ConfirmationNumberIcon from "@mui/icons-material/ConfirmationNumber";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import AddIcon from "@mui/icons-material/Add";
import CancelIcon from "@mui/icons-material/Cancel";
import { getBpeList } from "../../api/bpeApi";
import { formatCpfDisplay } from "../../api/bpePassengerClients";
import BPeWizard from "./BPeWizard";
import BPeCancelDialog from "./BPeCancelDialog";
import BPePurchaseJourney from "./BPePurchaseJourney";

/** Lê dicas opcionais nas tags: bpe_origem:... e bpe_destino:... */
function parseBpeTags(tags) {
  const out = { origem: "", destino: "" };
  if (!tags || typeof tags !== "string") return out;
  for (const raw of tags.split(/[,\n;]+/)) {
    const p = raw.trim();
    const low = p.toLowerCase();
    if (low.startsWith("bpe_origem:")) out.origem = p.slice(p.indexOf(":") + 1).trim();
    if (low.startsWith("bpe_destino:")) out.destino = p.slice(p.indexOf(":") + 1).trim();
  }
  return out;
}

const PassagensBPe = ({ selectedConversation, onReloadMessages }) => {
  const { t } = useTranslation("chat");
  const conversationId = selectedConversation?.id ?? selectedConversation?.id_lead;
  const [bps, setBps] = useState(selectedConversation?.bpes ?? selectedConversation?.passagens ?? []);
  const [loadingBps, setLoadingBps] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [cancelTicket, setCancelTicket] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "" });
  const [journeyKey, setJourneyKey] = useState(0);

  const { origem: hintOrigem, destino: hintDestino } = useMemo(
    () => parseBpeTags(selectedConversation?.tags),
    [selectedConversation?.tags]
  );
  const hasRouteHint = Boolean(hintOrigem || hintDestino);

  const clientPrefill = useMemo(
    () => ({
      clientId: selectedConversation?.clientId ? Number(selectedConversation.clientId) : null,
      projectId: selectedConversation?.projectId ?? selectedConversation?.id_projeto ?? null,
      nome: selectedConversation?.nome?.trim() || "",
      email: selectedConversation?.email?.trim() || "",
      celular: selectedConversation?.celular?.trim() || "",
      documento: formatCpfDisplay(selectedConversation?.cpf) || "",
    }),
    [
      selectedConversation?.clientId,
      selectedConversation?.projectId,
      selectedConversation?.id_projeto,
      selectedConversation?.nome,
      selectedConversation?.email,
      selectedConversation?.celular,
      selectedConversation?.cpf,
    ]
  );

  const loadBpes = useCallback(async () => {
    if (!conversationId) return;
    setLoadingBps(true);
    setLoadError(false);
    try {
      const list = await getBpeList(conversationId);
      setBps(Array.isArray(list) ? list : []);
    } catch {
      setBps([]);
      setLoadError(true);
    } finally {
      setLoadingBps(false);
    }
  }, [conversationId]);

  useEffect(() => {
    if (conversationId) loadBpes();
    else setBps([]);
  }, [conversationId, loadBpes]);

  const tripSummaryText = useMemo(() => {
    if (!Array.isArray(bps) || !bps.length) return "";
    return bps
      .map((bp) => {
        const parts = [
          `${bp.origem ?? bp.origem_viagem} → ${bp.destino ?? bp.destino_viagem}`,
          bp.dataviagem ? bp.dataviagem : null,
          bp.localizador ? `${t("passagensBpe.locator")}: ${bp.localizador}` : null,
          bp.poltrona ? `${t("passagensBpe.seat")}: ${bp.poltrona}` : null,
        ].filter(Boolean);
        return parts.join(" · ");
      })
      .join("\n");
  }, [bps, t]);

  const handleCopySummary = async () => {
    if (!tripSummaryText) return;
    let copied = false;
    try {
      await navigator.clipboard.writeText(tripSummaryText);
      copied = true;
    } catch {
      /* ignore */
    }
    if (conversationId) {
      window.dispatchEvent(
        new CustomEvent("xpro-chat-compose", {
          detail: {
            conversationId: Number(conversationId),
            appendText: tripSummaryText,
          },
        })
      );
      setSnackbar({ open: true, message: t("passagensBpe.copySuccess") });
    } else if (copied) {
      setSnackbar({ open: true, message: t("passagensBpe.copyClipboardOnly") });
    }
  };

  const handleWizardSuccess = () => {
    loadBpes();
    setJourneyKey((k) => k + 1);
    if (typeof onReloadMessages === "function") {
      onReloadMessages();
    }
  };

  if (!conversationId) {
    return (
      <Typography variant="body2" color="text.secondary">
        {t("passagensBpe.noConversation")}
      </Typography>
    );
  }

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      {loadingBps ? (
        <Box display="flex" justifyContent="center" py={2}>
          <CircularProgress size={24} />
        </Box>
      ) : loadError ? (
        <Alert severity="warning" action={<Button size="small" onClick={loadBpes}>{t("passagensBpe.retry")}</Button>}>
          {t("passagensBpe.loadError")}
        </Alert>
      ) : Array.isArray(bps) && bps.length > 0 ? (
        <Box>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            {t("passagensBpe.listTitle")}
          </Typography>
          <Stack spacing={1}>
            {bps.map((bp, index) => (
              <Box
                key={bp.id ?? index}
                sx={{
                  py: 1.25,
                  px: 1.5,
                  borderRadius: 1,
                  bgcolor: "action.hover",
                  borderLeft: 3,
                  borderColor: bp.cancelado ? "error.main" : "primary.main",
                  opacity: bp.cancelado ? 0.85 : 1,
                }}
              >
                <Typography variant="body2" fontWeight={600}>
                  {bp.origem ?? bp.origem_viagem} → {bp.destino ?? bp.destino_viagem}
                  {bp.cancelado ? ` (${t("passagensBpe.statusCancelled")})` : ""}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  {[bp.dataviagem, bp.localizador && `${t("passagensBpe.locator")}: ${bp.localizador}`, bp.poltrona && `${t("passagensBpe.seat")}: ${bp.poltrona}`]
                    .filter(Boolean)
                    .join(" · ")}
                </Typography>
                {!bp.cancelado && (
                <Button
                  size="small"
                  color="error"
                  startIcon={<CancelIcon />}
                  onClick={() => setCancelTicket(bp)}
                  sx={{ textTransform: "none", mt: 0.75, p: 0, minHeight: 0 }}
                >
                  {t("passagensBpe.cancelTicket")}
                </Button>
                )}
              </Box>
            ))}
          </Stack>
          <Button
            size="small"
            variant="outlined"
            startIcon={<ContentCopyIcon />}
            onClick={handleCopySummary}
            fullWidth
            sx={{ textTransform: "none", mt: 1 }}
          >
            {t("passagensBpe.copySummary")}
          </Button>
        </Box>
      ) : (
        <Box
          sx={{
            py: 2,
            px: 1.5,
            borderRadius: 1,
            bgcolor: "action.hover",
            textAlign: "center",
          }}
        >
          <ConfirmationNumberIcon color="action" sx={{ fontSize: 36, mb: 0.5 }} />
          <Typography variant="subtitle2" gutterBottom>
            {t("passagensBpe.emptyTitle")}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t("passagensBpe.emptyHint")}
          </Typography>
        </Box>
      )}

      {hasRouteHint && (
        <Alert severity="info" sx={{ py: 0.5 }}>
          <Typography variant="caption">{t("passagensBpe.routeHint")}</Typography>
        </Alert>
      )}

      <BPePurchaseJourney conversationId={conversationId} refreshKey={journeyKey} />

      <Button
        variant="contained"
        startIcon={bps.length > 0 ? <AddIcon /> : <ConfirmationNumberIcon />}
        onClick={() => setWizardOpen(true)}
        fullWidth
        size="medium"
        sx={{ textTransform: "none", fontWeight: 600 }}
      >
        {t("passagensBpe.sellTicket")}
      </Button>

      <BPeWizard
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        conversationId={conversationId}
        onSuccess={handleWizardSuccess}
        initialOrigem={hintOrigem}
        initialDestino={hintDestino}
        clientPrefill={clientPrefill}
        channelInfo={{
          channelId: selectedConversation?.channelId ?? null,
          channelType: selectedConversation?.channelType ?? null,
          projectId: clientPrefill?.projectId ?? null,
          phone: clientPrefill?.celular ?? "",
        }}
      />

      <BPeCancelDialog
        open={Boolean(cancelTicket)}
        onClose={() => setCancelTicket(null)}
        conversationId={conversationId}
        ticket={cancelTicket}
        onCanceled={() => {
          loadBpes();
          setJourneyKey((k) => k + 1);
          setSnackbar({ open: true, message: t("passagensBpe.cancelSuccess") });
        }}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ open: false, message: "" })}
        message={snackbar.message}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </Box>
  );
};

export default PassagensBPe;
